import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GuiFlowService } from '../../___LOGIC/__gui/gui-flow.service';
import { CrudMeta, FkOption, CrudService, CrudMetaColumn } from 'src/app/___LOGIC/__crud/crud.service';

@Component({
  selector: 'app-crud-dynamic',
  templateUrl: './crud-dynamic.component.html',
  styleUrls: ['./crud-dynamic.component.css']
})
export class CrudDynamicComponent implements OnInit, OnDestroy {

  table = '';
  meta: CrudMeta | null = null;
  loading = false;

  // listado
  rows: any[] = [];
  total = 0;
  page = 1;
  pageSize = 10;
  sort = '';
  dir: 'asc' | 'desc' | '' = '';
  search = '';

  // formulario
  form: FormGroup | null = null;
  editing: any = null; // fila en edición

  // opciones de FK: { [colName]: FkOption[] }
  fkOptions: Record<string, FkOption[]> = {};

  private sub?: Subscription;

  fkLabelFields: Record<string, string[]> = {
  // ejemplo:
  // 'FK_ID_answer': ['code','name'],
  'FK_ID_clientDetails': ['name','lastname','goal']
  };

  crudStatus = 'read';

  constructor(private crudService: CrudService,private route: ActivatedRoute,private fb: FormBuilder,private guiFlow: GuiFlowService) {}

  ngOnInit() {
    this.sub = this.route.paramMap.subscribe(p => {
      this.table = p.get('table') || '';
      this.resetState();
      this.loadMeta();
    });
  }

  ngOnDestroy() { this.sub?.unsubscribe(); }

  resetState() {
    this.rows = [];
    this.total = 0;
    this.page = 1;
    this.sort = '';
    this.dir = '';
    this.search = '';
    this.form = null;
    this.editing = null;
    this.fkOptions = {};
  }

  loadMeta() {
    this.loading = true;
    this.crudService.getMeta(this.table).subscribe({
      next: (meta) => {
        this.meta = meta;
        this.buildForm(meta);
        this.loadForeignOptions(meta); // cargar opciones para FKs
        this.loadData();
      },
      error: (errorObj) => {
        this.loading = false
        console.log(errorObj.error);
        this.guiFlow.reportError(errorObj.error,'crud-dynamic-component');
      }
    });
  }

  private isFkByName(c: CrudMetaColumn) {
    return /^FK_ID_/i.test(c.name);
  }

  private buildForm(meta: CrudMeta) {
    const group: any = {};
    meta.columns.forEach(c => {
      if (c.isPrimaryKey) return;
      const validators: any[] = [];
      if (!c.isNullable) validators.push(Validators.required);
      if (c.maxLength) validators.push(Validators.maxLength(c.maxLength));
      group[c.name] = [c.defaultValue ?? '', validators];
    });
    this.form = this.fb.group(group);
  }

  private loadForeignOptions(meta: CrudMeta) {
    const fkCols = meta.columns.filter(c => (c.isForeignKey || this.isFkByName(c)) && !c.isPrimaryKey);
    if (!fkCols.length) return;

    const calls = fkCols.map(c =>
      this.crudService.getFkOptions(this.table, c.name).pipe(
        catchError(() => of([] as FkOption[])),
        map(list => ({ col: c.name, list }))
      )
    );

    forkJoin(calls).subscribe(results => {
      results.forEach(r => this.fkOptions[r.col] = r.list);
    });
  }

  loadFKOptions(meta: CrudMeta) {
    meta.columns.forEach(col => {
      if (col.name.startsWith('FK_ID_')) {
        const labelFields = this.fkLabelFields[col.name];
        this.crudService.getFkOptions(this.table, col.name, labelFields).subscribe(list => {
          // list: [{id,label,data}]
          this.fkOptions[col.name] = list;
        });
      }
    });
  }

  goBack(){
    
  }

  reload(){
    debugger
  }

  loadData() {
    this.loading = true;
    this.crudService
      .list(this.table, this.page, this.pageSize, this.sort, this.dir, this.search)
      .subscribe({
        next: (res) => {
          this.rows = res.rows;
          this.total = res.total;
          this.loading = false;
        },
        error: () => this.loading = false
      });
  }

  sortBy(col: string) {
    if (this.sort === col) {
      this.dir = this.dir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sort = col;
      this.dir = 'asc';
    }
    this.loadData();
  }

  onCreate() {
    if (!this.meta || !this.form) return;
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }
    const data = this.form.value;
    this.crudService.create(this.table, data).subscribe(
    (resp) => {
      this.crudStatus = 'read';
      this.form!.reset();
      this.loadData();
      this.guiFlow.reportSuccess('Created successfully');
    },
    (error) => {
      this.guiFlow.reportError(error,'@crud-dynamic when create');
    });
  }

  onEdit(row: any) {
    this.editing = { ...row };
    this.crudStatus = 'update';
    if (!this.meta || !this.form) return;
    const values: any = {};
    this.meta.columns.forEach(c => { if (!c.isPrimaryKey) values[c.name] = row[c.name]; });
    this.form.patchValue(values);
  }

  onCancelEdit() {
    this.editing = null;
    this.form?.reset();
    this.crudStatus = 'read';
  }

  onUpdate() {
    if (!this.meta || !this.form || !this.editing) return;
    const pk = this.meta.primaryKey;
    const id = this.editing[pk];
    const data = this.form.value;
    this.crudService.update(this.table, pk, id, data).subscribe(
      (resp) => {
        this.editing = null;
        this.form!.reset();
        this.loadData();
        this.crudStatus = 'read';
        this.guiFlow.reportSuccess('Updated successfully');
      },
      (error) => {
        this.guiFlow.reportError(error,'@crud-dynamic when update');
      }
    );
  }

  onDelete(row: any) {
    if (!this.meta) return;
    const pk = this.meta.primaryKey;
    if (!confirm('¿Seguro que quieres eliminar este registro?')) return;
    this.crudService.remove(this.table, pk, row[pk]).subscribe(() => this.loadData());
  }

  displayLabel(col: CrudMetaColumn) {
    return col.name.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
  }

  isEnum(col: CrudMetaColumn) { return col.dataType === 'enum' && !!col.enumValues?.length; }
  isTextArea(col: CrudMetaColumn) { return ['text', 'mediumtext', 'longtext'].includes(col.dataType); }
  isDate(col: CrudMetaColumn) { return ['date', 'datetime', 'timestamp'].includes(col.dataType); }
  isNumeric(col: CrudMetaColumn) { return ['int', 'bigint', 'decimal', 'float', 'double'].includes(col.dataType); }
  isBoolean(col: CrudMetaColumn) { return ['tinyint', 'bool', 'boolean'].includes(col.dataType); }
  isForeign(col: CrudMetaColumn) { return !!col.isForeignKey || this.isFkByName(col); }
}
